import { useForm } from "@tanstack/react-form";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { MutationConfirmPreview } from "@/core/components/shared/preview-action";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import { useCreateMerchant } from "@/features/merchant/api/use-create-merchant";
import { useCreateSubMerchant } from "@/features/merchant/api/use-create-sub-merchant";
import type {
  CreateMerchantPayload,
  CreateSubMerchantPayload,
  MerchantDto,
} from "@/features/merchant/api/dto";
import { useMerchantTranslation } from "@/features/merchant/i18n/use-merchant-translation";
import {
  createMerchantSchema,
  type MerchantFormValues,
} from "@/features/merchant/schemas/merchant.schema";
import { usePartySelectOptions } from "@/features/shared/api";

const EMPTY_VALUES: MerchantFormValues = {
  ownerPartyId: "",
  legalName: "",
  tradeName: "",
  categoryCode: "",
};

const toPayload = (
  values: MerchantFormValues
): CreateMerchantPayload | CreateSubMerchantPayload => ({
  ownerPartyId: values.ownerPartyId.trim(),
  legalName: values.legalName.trim(),
  tradeName: values.tradeName?.trim() || undefined,
  categoryCode: values.categoryCode?.trim() || undefined,
});

/**
 * Formulaire de création d'un marchand (`POST /Merchants/add`) — idempotent.
 * Peut aussi créer un sous-marchand (`POST /Merchants/{masterId}/sub-merchants`)
 * quand `masterId` est fourni. Pas d'argent en jeu à la création : pas de
 * `ShareablePreview` (comme WalletFormDialog).
 */
export function MerchantFormDialog({
  open,
  onOpenChange,
  onCreated,
  masterId,
  actingMerchantId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (merchant: MerchantDto) => void;
  /** Si fourni, crée un sous-marchand rattaché à ce master. */
  masterId?: string;
  /** Query param `actingMerchantId` (self-service merchant). */
  actingMerchantId?: string;
}) {
  const { t } = useTypedTranslation();
  const { t: tm } = useMerchantTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue.
  const schema = useMemo(() => createMerchantSchema(), [i18n.language]);

  const createMerchant = useCreateMerchant();
  const createSubMerchant = useCreateSubMerchant(masterId ?? "");
  const pending = masterId
    ? createSubMerchant.isPending
    : createMerchant.isPending;
  const [preview, setPreview] = useState<
    CreateMerchantPayload | CreateSubMerchantPayload | null
  >(null);
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      const payload = toPayload(parsed.data);
      setPreview(payload);
    },
  });

  const fieldError =
    (name: keyof MerchantFormValues) => (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        Modal création : header + footer figés, zone de champs scrollable.
        - `flex-col` + `overflow-hidden` sur DialogContent remplacent le
          `grid` + scroll global du conteneur (sinon le footer scrolle avec
          les champs et peut sortir du viewport).
        - La zone des fields a `overflow-y-auto min-h-0 flex-1` pour que le
          scroll kicke dès que la hauteur du formulaire dépasse `max-h-[90dvh]`.
      */}
      <DialogContent className="flex max-h-[90dvh] flex-col gap-4 overflow-hidden sm:max-w-3xl">
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {masterId
              ? tm("merchant.merchants.subMerchants.create")
              : tm("merchant.merchants.create")}
          </DialogTitle>
        </DialogHeader>
        <form
          className="flex min-h-0 flex-1 flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <div className="show-scrollbar grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto pr-1 sm:grid-cols-2">
          <form.Field
            name="ownerPartyId"
            validators={{ onChange: fieldError("ownerPartyId") }}
          >
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="ownerPartyId">
                  {tm("merchant.merchants.form.ownerPartyId")}
                </Label>
                <EntitySearchableSelect
                  id="ownerPartyId"
                  options={partyOptions}
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value)}
                  placeholder={tm(
                    "merchant.merchants.form.ownerPartyIdPlaceholder"
                  )}
                  isLoading={partiesLoading}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field
            name="legalName"
            validators={{ onChange: fieldError("legalName") }}
          >
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="legalName">
                  {tm("merchant.merchants.form.legalName")}
                </Label>
                <Input
                  id="legalName"
                  value={field.state.value}
                  placeholder={tm(
                    "merchant.merchants.form.legalNamePlaceholder"
                  )}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <form.Field name="tradeName">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="tradeName">
                  {tm("merchant.merchants.form.tradeName")}
                </Label>
                <Input
                  id="tradeName"
                  value={field.state.value ?? ""}
                  placeholder={tm(
                    "merchant.merchants.form.tradeNamePlaceholder"
                  )}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
              </div>
            )}
          </form.Field>

          <form.Field name="categoryCode">
            {(field) => (
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="categoryCode">
                  {tm("merchant.merchants.form.categoryCode")}
                </Label>
                <Input
                  id="categoryCode"
                  className="notranslate uppercase"
                  value={field.state.value ?? ""}
                  placeholder={tm(
                    "merchant.merchants.form.categoryCodePlaceholder"
                  )}
                  onBlur={field.handleBlur}
                  onChange={(e) =>
                    field.handleChange(e.target.value.toUpperCase())
                  }
                />
              </div>
            )}
          </form.Field>
          </div>

          <DialogFooter className="shrink-0 border-t pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
    <MutationConfirmPreview
      open={preview !== null}
      onOpenChange={(isOpen) => {
        if (!isOpen) setPreview(null);
      }}
      data={preview}
      title={t("common.mutationPreview.createTitle")}
      description={t("common.mutationPreview.createDescription")}
      t={tm}
      pending={pending}
      onConfirm={() => {
        if (!preview) return;
        if (masterId) {
          createSubMerchant.mutate(
            { body: preview, actingMerchantId },
            {
              onSuccess: (data) => {
                notifSilence(tm("merchant.merchants.feedback.subMerchantCreated"));
                onCreated?.(data);
                setPreview(null);
                onOpenChange(false);
              },
              onError: (error) => notifSilenceError(error.message),
            }
          );
        } else {
          createMerchant.mutate(preview as CreateMerchantPayload, {
            onSuccess: (data) => {
              notifSilence(tm("merchant.merchants.feedback.created"));
              onCreated?.(data);
              setPreview(null);
              onOpenChange(false);
            },
            onError: (error) => notifSilenceError(error.message),
          });
        }
      }}
    />
    </>
  );
}
